// frontend/src/hooks/useVersionCheck.js
import { useEffect, useRef, useState, useCallback } from 'react';
import { healthApi, deploymentsApi } from '../utils/api';
import { getDismissedId, setDismissedId } from '../utils/updateDismissal';
import { deriveToastState, ACTIVE_PHASES } from '../utils/updateToastState';

const LOADED_COMMIT = process.env.REACT_APP_COMMIT_SHA || null;
const HEALTH_MS = 60_000;
const DEPLOY_IDLE_MS = 30_000;
const DEPLOY_ACTIVE_MS = 10_000;

const isActivePhase = (p) => ACTIVE_PHASES.includes(p);

// Build the comparable id from a /health payload (commit preferred).
const idFromHealth = (data) => {
  const commit = data?.commitShort;
  if (commit && commit !== 'unknown') return `c:${commit}`;
  if (data?.version) return `v:${data.version}`;
  return null;
};

/**
 * Drives the global update toast. Returns:
 *   status: 'hidden' | 'deploying' | 'ready'
 *   deployment: latest /api/deployments/current payload (or null)
 *   loadedCommit: REACT_APP_COMMIT_SHA (or null)
 *   justDeployed: true when a deploy we watched just finished (show 100% + refresh)
 *   refresh(): reload into the new bundle
 *   dismiss(): silence the current target id until the next deploy
 */
export function useVersionCheck() {
  const initialIdRef = useRef(null);
  const prevPhaseRef = useRef(null);
  const [liveId, setLiveId] = useState(null);
  const [deployment, setDeployment] = useState(null);
  const [completedDeployId, setCompletedDeployId] = useState(null);
  const [dismissTick, setDismissTick] = useState(0); // bump to re-evaluate after dismiss

  // --- /health poll: detect "a newer build is live than the one we loaded" ---
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const data = await healthApi.check();
        if (cancelled) return;
        const id = idFromHealth(data);
        if (!id) return;
        if (initialIdRef.current === null) initialIdRef.current = id;
        setLiveId(id);
      } catch {
        /* network blip — retry next tick */
      }
    };
    check();
    const timer = setInterval(check, HEALTH_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  // --- /api/deployments/current poll: drive the deploying state + progress ---
  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      try {
        const data = await deploymentsApi.getCurrent();
        if (cancelled) return;
        const next = data && data.enabled === false ? null : data;
        // A deploy we were watching just finished: active phase -> ACTIVE.
        // Mark it so the toast shows 100% + the refresh prompt immediately,
        // instead of waiting on the slower /health poll to notice.
        if (isActivePhase(prevPhaseRef.current) && next?.phase === 'ACTIVE' && next?.deployment_id) {
          setCompletedDeployId(next.deployment_id);
        }
        prevPhaseRef.current = next?.phase || null;
        setDeployment(next);
        const fast = next && isActivePhase(next.phase);
        timer = setTimeout(tick, fast ? DEPLOY_ACTIVE_MS : DEPLOY_IDLE_MS);
      } catch {
        if (!cancelled) timer = setTimeout(tick, DEPLOY_IDLE_MS);
      }
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, []);

  // --- derive status (pure) ---
  void dismissTick; // read so the derivation re-runs after a dismiss
  const { status, targetId, justDeployed } = deriveToastState({
    deployment,
    liveId,
    initialId: initialIdRef.current,
    completedDeployId,
    dismissedId: getDismissedId(),
  });

  const dismiss = useCallback(() => {
    if (targetId) setDismissedId(targetId);
    setDismissTick((t) => t + 1);
  }, [targetId]);

  const refresh = useCallback(() => window.location.reload(), []);

  return { status, deployment, loadedCommit: LOADED_COMMIT, justDeployed, refresh, dismiss };
}

export default useVersionCheck;
